import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { analyticsReportOptions, productInstancesByCustomerOptions } from "@dashboard/api/queryApi";
import useUser from "@dashboard/hooks/useUser";

export interface PageData {
    rank: number;
    pageName: string;
    pagePath: string;
    views: number;
    changePercent: number | null;
    externalUrl: string;
}

const DEV_MOCK_PAGES: PageData[] = [
    {
        rank: 1,
        pageName: "Forside",
        pagePath: "/cp/mock-kunde/start/",
        views: 1243,
        changePercent: 26,
        externalUrl: "#",
    },
    {
        rank: 2,
        pageName: "Nyheter",
        pagePath: "/cp/mock-kunde/nyheter/",
        views: 892,
        changePercent: -13,
        externalUrl: "#",
    },
    {
        rank: 3,
        pageName: "Produkter",
        pagePath: "/cp/mock-kunde/produkter/",
        views: 754,
        changePercent: 23,
        externalUrl: "#",
    },
    {
        rank: 4,
        pageName: "Om oss",
        pagePath: "/cp/mock-kunde/om-oss/",
        views: 531,
        changePercent: 9,
        externalUrl: "#",
    },
    {
        rank: 5,
        pageName: "Kontakt",
        pagePath: "/cp/mock-kunde/kontakt/",
        views: 413,
        changePercent: 4,
        externalUrl: "#",
    },
    {
        rank: 6,
        pageName: "FAQ",
        pagePath: "/cp/mock-kunde/faq/",
        views: 387,
        changePercent: 93,
        externalUrl: "#",
    },
    {
        rank: 7,
        pageName: "Tjenester",
        pagePath: "/cp/mock-kunde/tjenester/",
        views: 312,
        changePercent: -10,
        externalUrl: "#",
    },
    {
        rank: 8,
        pageName: "Blogg",
        pagePath: "/cp/mock-kunde/blogg/",
        views: 278,
        changePercent: 78,
        externalUrl: "#",
    },
    {
        rank: 9,
        pageName: "Ansatte",
        pagePath: "/cp/mock-kunde/ansatte/",
        views: 241,
        changePercent: -10,
        externalUrl: "#",
    },
    {
        rank: 10,
        pageName: "Karriere",
        pagePath: "/cp/mock-kunde/karriere/",
        views: 198,
        changePercent: 38,
        externalUrl: "#",
    },
];

function getDateRanges() {
    const now = new Date();

    const currentEnd = new Date(now.getFullYear(), now.getMonth(), 1);
    currentEnd.setDate(currentEnd.getDate() - 1);

    const currentStart = new Date(currentEnd.getFullYear(), currentEnd.getMonth() - 2, 1);

    const previousEnd = new Date(currentStart);
    previousEnd.setDate(previousEnd.getDate() - 1);

    const previousStart = new Date(previousEnd.getFullYear(), previousEnd.getMonth() - 2, 1);

    return { currentStart, currentEnd, previousStart, previousEnd };
}

function formatPeriodLabel(start: Date, end: Date, locale: string): string {
    const fmt = (d: Date) => d.toLocaleDateString(locale, { month: "short", year: "numeric" });
    return `${fmt(start)}–${fmt(end)}`;
}

export function useMostVisitedPages() {
    const [selectedProductUrl, setSelectedProductUrl] = useState<string | null>(null);
    const { i18n } = useTranslation();
    const locale = i18n.language || "nb";

    const user = useUser();
    const customerId = user?.ownedByCustomerId ?? null;

    const {
        data: productInstances,
        isLoading: productsLoading,
        isError: productsError,
    } = useQuery(productInstancesByCustomerOptions(customerId));

    const { currentStart, currentEnd, previousStart, previousEnd } = useMemo(
        () => getDateRanges(),
        [],
    );

    const {
        data: currentReport,
        isLoading: currentReportLoading,
        isError: currentReportError,
    } = useQuery(analyticsReportOptions(customerId, currentStart, currentEnd));

    const {
        data: previousReport,
        isLoading: previousReportLoading,
        isError: previousReportError,
    } = useQuery(analyticsReportOptions(customerId, previousStart, previousEnd));

    const useMockData = import.meta.env.DEV && !!customerId && currentReportError;

    const pages = useMemo(() => {
        if (useMockData) return DEV_MOCK_PAGES;
        if (!currentReport?.rows) return [];

        const pathIndex =
            currentReport.dimensionHeaders?.findIndex((h) => h.name === "unifiedPagePathScreen") ??
            -1;
        const nameIndex =
            currentReport.dimensionHeaders?.findIndex((h) => h.name === "unifiedScreenName") ?? -1;

        if (pathIndex === -1 || nameIndex === -1) return [];

        const currentViews = new Map<
            string,
            { views: number; pageName: string; pagePath: string; productUrl: string }
        >();

        for (const row of currentReport.rows) {
            const pagePath = row.dimensionValues?.[pathIndex]?.value;
            const screenName = row.dimensionValues?.[nameIndex]?.value;
            const viewsRaw = row.metricValues?.[0]?.value;
            if (!pagePath || !screenName || viewsRaw == null) continue;

            const views = Number(viewsRaw);
            if (!Number.isFinite(views)) continue;

            const splitName = screenName.split("::");
            const pageName = splitName.length > 2 ? splitName.slice(-1)[0] : splitName[0];
            const productUrl = pagePath.split("/")[2] || "";

            const existing = currentViews.get(pagePath);
            if (existing) {
                existing.views += views;
            } else {
                currentViews.set(pagePath, { views, pageName, pagePath, productUrl });
            }
        }

        const previousViews = new Map<string, number>();
        if (previousReport?.rows) {
            const prevPathIndex =
                previousReport.dimensionHeaders?.findIndex(
                    (h) => h.name === "unifiedPagePathScreen",
                ) ?? -1;
            if (prevPathIndex !== -1) {
                for (const row of previousReport.rows) {
                    const pagePath = row.dimensionValues?.[prevPathIndex]?.value;
                    const viewsRaw = row.metricValues?.[0]?.value;
                    if (!pagePath || viewsRaw == null) continue;

                    const views = Number(viewsRaw);
                    if (!Number.isFinite(views)) continue;

                    previousViews.set(pagePath, (previousViews.get(pagePath) ?? 0) + views);
                }
            }
        }

        let entries = Array.from(currentViews.values());

        if (selectedProductUrl) {
            entries = entries.filter((e) => e.productUrl === selectedProductUrl);
        }

        entries.sort((a, b) => b.views - a.views);
        const top10 = entries.slice(0, 10);

        return top10.map((entry, idx) => {
            const prevViews = previousViews.get(entry.pagePath);
            let changePercent: number | null = null;
            if (prevViews != null && prevViews > 0) {
                changePercent = ((entry.views - prevViews) / prevViews) * 100;
            }

            return {
                rank: idx + 1,
                pageName: entry.pageName || entry.pagePath,
                pagePath: entry.pagePath,
                views: entry.views,
                changePercent,
                externalUrl: `https://cp.compendia.no${entry.pagePath}`,
            };
        });
    }, [currentReport, previousReport, selectedProductUrl, useMockData]);

    const totalViews = useMemo(() => pages.reduce((sum, p) => sum + p.views, 0), [pages]);

    const periodLabel = useMemo(
        () => formatPeriodLabel(currentStart, currentEnd, locale),
        [currentStart, currentEnd, locale],
    );

    const isLoading = productsLoading || currentReportLoading || previousReportLoading;

    const missingAnalyticsKey = !import.meta.env.DEV && !!customerId && currentReportError;

    const isError =
        productsError ||
        (!useMockData && !missingAnalyticsKey && (currentReportError || previousReportError));

    return {
        pages,
        products: (productInstances ?? []).filter((p) => p.compublishId),
        totalViews,
        periodLabel,
        isLoading,
        isError,
        useMockData: !!useMockData,
        missingAnalyticsKey,
        selectedProductUrl,
        setSelectedProductUrl,
    };
}
